import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string) => void;
  compact?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, helperText, options, placeholder, id, onChange, value, compact = false, ...props }, ref) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block font-medium',
              'text-gray-700 dark:text-secondary-300',
              compact ? 'mb-1 text-xs' : 'mb-1.5 text-sm'
            )}
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            value={value}
            onChange={handleChange}
            className={cn(
              'flex w-full appearance-none rounded-lg border text-sm transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              // Compact size
              compact ? 'h-8 px-2.5 py-1.5 pr-8' : 'h-9 px-3 py-2 pr-9',
              // Light mode
              'bg-white border-gray-300',
              'focus:border-primary-500 focus:ring-primary-500/20',
              'disabled:bg-gray-50',
              // Dark mode
              'dark:bg-secondary-900/50 dark:border-white/[0.1]',
              'dark:text-secondary-100',
              'dark:focus:border-primary-500/50 dark:focus:ring-primary-500/20',
              'dark:focus:bg-secondary-900/80',
              'dark:disabled:bg-secondary-900/30',
              // Error state
              error && cn(
                'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20',
                'dark:border-danger-500/50 dark:focus:border-danger-500/70'
              ),
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled className="dark:bg-secondary-900">
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="dark:bg-secondary-900"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 dark:text-secondary-500">
            <ChevronDown className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
          </div>
        </div>
        {error && (
          <p className={cn('text-danger-600 dark:text-danger-400', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className={cn('text-gray-500 dark:text-secondary-500', compact ? 'mt-1 text-xs' : 'mt-1.5 text-sm')}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
